<template>
  <!-- Teleport 하지 않음: .admin 셸 안에서 미리보기 밀도가 맞는다 -->
  <div class="spform aedit-modal" role="presentation" @keydown.esc.prevent="onEsc" @keydown="onHotkey">
    <div class="spform__backdrop" @click="onEsc" />
    <div
      ref="dialogEl"
      class="spform__dialog"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      tabindex="-1"
    >
      <header class="spform__head" data-tour="admin-product-summary">
        <div class="spform__head-top">
          <div class="spform__title-row">
            <h2 :id="titleId">상품 검수</h2>
            <div class="spform__meta-badges">
              <span v-if="code" class="spform__code-badge">
                <AppIcon icon="mdi-barcode" :size="14" />
                {{ code }}
              </span>
              <span v-if="product" class="sui-badge" :class="statusTone">
                {{ product.status === 'REQUESTED' ? '검수요청' : MALL_PRODUCT_STATUS_LABEL[product.status] }}
              </span>
              <span v-if="product?.storeName" class="aedit-modal__store-badge">
                <AppIcon icon="mdi-storefront-outline" :size="14" />
                {{ product.storeName }}
              </span>
            </div>
          </div>

          <div class="spform__head-actions">
            <button
              v-if="product && !loading"
              type="button"
              class="aedit-modal__history-btn"
              data-tour="admin-product-history"
              :disabled="saving || screenLock"
              @click="historyOpen = true"
            >
              이력
              <em v-if="logs.length">{{ logs.length }}</em>
            </button>
            <button type="button" class="spform__close" aria-label="닫기" :disabled="saving || screenLock" @click="onClose">
              <AppIcon icon="mdi-close" :size="20" />
            </button>
          </div>
        </div>
      </header>

      <div v-if="loading" class="spform__loading">
        <AppIcon icon="mdi-loading" :size="28" />
        <p>상품을 불러오는 중입니다…</p>
      </div>

      <template v-else>
        <div class="spform__body spform__body--register" :class="{ 'spform__body--sale': form.mallKind === 'SALE' }">
          <div class="spform__main">
            <ProductRegisterSheet
              :form="form"
              tone="admin"
              :store-name="product?.storeName || ''"
              :categories="[...SELLER_CATEGORIES]"
              :uploader="uploadAdminImages"
              :store-shipping="storeShipping"
              store-shipping-readonly
              :stock-locked="stockLocked"
              :sale-status="product?.status || ''"
              :can-edit-status="canToggleSaleStatus"
              :is-new="false"
              @notify="notify"
              @open-stock-history="stockHistoryOpen = true"
              @update-status="onSheetStatus"
            />

            <section
              v-if="showQueueNav"
              class="aedit-modal__queue"
              data-tour="admin-product-queue-nav"
            >
              <p class="aedit-modal__queue-label">
                검수요청
                <em v-if="queueIndex >= 0">{{ queueIndex + 1 }} / {{ queueCodes.length }}</em>
              </p>
              <div class="aedit-modal__queue-nav">
                <button
                  type="button"
                  class="sui-btn sui-btn--ghost sui-btn--sm"
                  :disabled="!prevCode || saving"
                  @click="goQueue(prevCode)"
                >
                  <AppIcon icon="mdi-chevron-left" :size="16" />
                  이전
                </button>
                <button
                  type="button"
                  class="sui-btn sui-btn--ghost sui-btn--sm"
                  :disabled="!nextCode || saving"
                  @click="goQueue(nextCode)"
                >
                  다음
                  <AppIcon icon="mdi-chevron-right" :size="16" />
                </button>
              </div>
            </section>

            <section
              class="aedit-modal__ops"
              data-tour="admin-product-ops"
            >
              <div class="aedit-modal__ops-block">
                <h3>내부 메모</h3>
                <p class="aedit-modal__ops-hint">판매업체·구매자에게 보이지 않습니다.</p>
                <textarea
                  v-model="adminMemo"
                  class="sui-input"
                  rows="3"
                  maxlength="1000"
                  placeholder="예: 원가 협의 중 · 패키지 변경 예정"
                />
              </div>

              <div class="aedit-modal__ops-block">
                <h3>관련 · 대체 상품</h3>
                <p class="aedit-modal__ops-hint">
                  상세 「관련상품」·품절 시 「대체상품」에 쓰입니다. 비우면 같은 분류 자동 추천입니다.
                </p>
                <label class="aedit-modal__ops-label" for="related-pick">관련상품 추가</label>
                <select id="related-pick" class="sui-select" :value="''" @change="addRelated($event)">
                  <option value="">판매중 상품 선택</option>
                  <option
                    v-for="row in relatedCandidates"
                    :key="`rel-${row.code}`"
                    :value="row.code"
                    :disabled="relatedCodes.includes(row.code) || substituteCodes.includes(row.code)"
                  >
                    {{ row.name }} ({{ row.code }})
                  </option>
                </select>
                <ul v-if="relatedCodes.length" class="aedit-modal__link-list">
                  <li v-for="row in relatedRows" :key="`r-${row.code}`">
                    <span>{{ row.name }} <em>{{ row.code }}</em></span>
                    <button type="button" class="sui-btn sui-btn--sm" @click="removeRelated(row.code)">
                      제거
                    </button>
                  </li>
                </ul>

                <label class="aedit-modal__ops-label" for="sub-pick">대체상품 추가</label>
                <select id="sub-pick" class="sui-select" :value="''" @change="addSubstitute($event)">
                  <option value="">판매중 상품 선택</option>
                  <option
                    v-for="row in relatedCandidates"
                    :key="`sub-${row.code}`"
                    :value="row.code"
                    :disabled="relatedCodes.includes(row.code) || substituteCodes.includes(row.code)"
                  >
                    {{ row.name }} ({{ row.code }})
                  </option>
                </select>
                <ul v-if="substituteCodes.length" class="aedit-modal__link-list">
                  <li v-for="row in substituteRows" :key="`s-${row.code}`">
                    <span>{{ row.name }} <em>{{ row.code }}</em></span>
                    <button type="button" class="sui-btn sui-btn--sm" @click="removeSubstitute(row.code)">
                      제거
                    </button>
                  </li>
                </ul>
              </div>
            </section>
          </div>

        </div>


        <footer class="spform__foot" data-tour="admin-product-actions">
          <button
            type="button"
            class="sui-btn sui-btn--ghost spform__foot-cancel"
            :disabled="saving"
            @click="onClose"
          >
            취소
          </button>

          <div class="spform__foot-end">
            <p
              v-if="firstProblem"
              class="spform__problem"
              role="button"
              tabindex="0"
              @click="onProblemJump"
              @keydown.enter.prevent="onProblemJump"
            >
              <AppIcon icon="mdi-alert-outline" :size="15" />
              {{ firstProblem }}
            </p>
            <div class="spform__actions">
              <details
                v-if="canReview"
                class="aedit-modal__reject"
                :open="product?.status === 'REJECTED'"
              >
                <summary>
                  <AppIcon icon="mdi-close-circle-outline" :size="16" />
                  <span>반려</span>
                </summary>
                <div class="aedit-modal__reject-panel">
                  <div class="aedit-modal__templates" role="group" aria-label="반려 사유 템플릿">
                    <button
                      v-for="tpl in rejectTemplates"
                      :key="tpl"
                      type="button"
                      class="aedit-modal__tpl"
                      @click="applyRejectTemplate(tpl)"
                    >
                      {{ tpl.slice(0, 18) }}{{ tpl.length > 18 ? '…' : '' }}
                    </button>
                  </div>
                  <textarea
                    v-model="rejectReason"
                    class="sui-input"
                    rows="3"
                    maxlength="255"
                    placeholder="어떤 점을 고쳐 다시 보내야 하는지 적어 주세요."
                  />
                  <button
                    type="button"
                    class="sui-btn aedit-modal__reject-btn"
                    :disabled="saving"
                    @click="onReject"
                  >
                    <AppIcon icon="mdi-alert-circle-outline" :size="16" />
                    <span>반려하고 판매업체에 알리기</span>
                  </button>
                </div>
              </details>
              <button type="button" class="sui-btn" :disabled="saving" @click="onSave()">
                <AppIcon icon="mdi-content-save-outline" :size="16" />
                <span>{{ saving ? '저장 중…' : '저장' }}</span>
              </button>
              <button
                v-if="canReview"
                type="button"
                class="sui-btn sui-btn--primary"
                :disabled="saving || !canApprove"
                @click="onApprove"
              >
                <AppIcon :icon="saving ? 'mdi-loading' : 'mdi-check-decagram'" :size="16" />
                <span>{{ saving ? '저장 중…' : '저장하고 판매 승인' }}</span>
              </button>
            </div>
          </div>
        </footer>
      </template>
    </div>

    <SellerProductPreview
      v-if="showPreview"
      :product="previewProduct"
      :active-zone="previewZone"
      @close="showPreview = false"
      @edit-zone="onCardEditZone"
    />

    <ProductStockHistoryModal
      v-if="stockHistoryOpen && code"
      :product-code="code"
      :product-name="form.name"
      audience="admin"
      @close="stockHistoryOpen = false"
      @updated="onStockLedgerUpdated"
    />

    <div
      v-if="historyOpen && product"
      class="aedit-hist"
      @keydown.esc.stop.prevent="historyOpen = false"
    >
      <button type="button" class="aedit-hist__catch" aria-label="검수 이력 닫기" @click="historyOpen = false" />
      <div
        ref="historyEl"
        class="aedit-hist__panel"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="historyTitleId"
        tabindex="-1"
      >
        <header class="aedit-hist__head">
          <div>
            <h2 :id="historyTitleId">검수 이력</h2>
            <p>
              검수 요청 {{ requestCount }}회
              · 최근 요청 {{ formatDateTime(product.requestedAt) }}
              · 최근 검수
              {{
                product.reviewedAt
                  ? `${formatDateTime(product.reviewedAt)} · ${product.reviewer}`
                  : '없음'
              }}
            </p>
          </div>
          <button type="button" class="aedit-hist__close" aria-label="검수 이력 닫기" @click="historyOpen = false">
            <AppIcon icon="mdi-close" :size="20" />
          </button>
        </header>

        <div class="aedit-hist__body">
          <div v-if="product.requestNote" class="spform__alert spform__alert--warn">
            <AppIcon icon="mdi-alert-circle-outline" :size="20" />
            <div>
              <strong>현재 요청 메모</strong>
              <p>{{ product.requestNote }}</p>
            </div>
          </div>

          <ol v-if="reviewLogs.length" class="aedit-modal__timeline">
            <li v-for="(log, index) in reviewLogs" :key="`${log.at}-${index}`">
              <div class="aedit-modal__timeline-meta">
                <span class="aedit-modal__log-action" :class="`is-${log.action.toLowerCase()}`">
                  {{ logLabel(log, index) }}
                </span>
                <time>{{ formatDateTime(log.at) }}</time>
              </div>
              <p class="aedit-modal__timeline-who">
                {{ log.role === 'ADMIN' ? '관리자' : '판매업체' }}
                {{ log.actor }}
              </p>
              <pre v-if="log.memo" class="aedit-modal__timeline-memo">{{ log.memo }}</pre>
              <p v-else class="aedit-modal__timeline-empty">남긴 내용 없음</p>
            </li>
          </ol>
          <p v-else class="aedit-modal__request-empty">아직 검수 요청·승인·반려 이력이 없습니다.</p>

          <details v-if="otherLogs.length" class="aedit-modal__history-more">
            <summary>그 외 이력 {{ otherLogs.length }}건</summary>
            <ul class="aedit-modal__logs">
              <li v-for="(log, index) in otherLogs" :key="`other-${log.at}-${index}`">
                <span class="aedit-modal__log-action" :class="`is-${log.action.toLowerCase()}`">
                  {{ LOG_LABEL[log.action] || log.action }}
                </span>
                <span>{{ log.role === 'ADMIN' ? '관리자' : '판매업체' }} {{ log.actor }}</span>
                <span>{{ log.memo || '-' }}</span>
                <time>{{ formatDateTime(log.at) }}</time>
              </li>
            </ul>
          </details>
        </div>

        <footer class="aedit-hist__foot">
          <button type="button" class="sui-btn sui-btn--ghost" @click="historyOpen = false">닫기</button>
        </footer>
      </div>
    </div>

    <div
      v-if="screenLock"
      class="aedit-busy"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span class="aedit-busy__icon">
        <AppIcon icon="mdi-loading" :size="36" />
      </span>
      <p>{{ screenLockText }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import ProductRegisterSheet from '@/components/manage/ProductRegisterSheet.vue'
import ProductStockHistoryModal from '@/components/manage/ProductStockHistoryModal.vue'
import SellerProductPreview from '@/components/seller/SellerProductPreview.vue'
import { useAdminProducts } from '@/composables/useAdminProducts'
import { confirmDialog } from '@/composables/useConfirmDialog'
import {
  approveAdminProduct,
  fetchAdminProduct,
  fetchAdminProducts,
  rejectAdminProduct,
  saveAdminProduct,
  saveAdminProductRelated,
  setAdminProductStatus,
  uploadAdminImages,
} from '@/api/admin'
import { MALL_PRODUCT_STATUS_LABEL, type MallProductStatus, type ServerMallProduct } from '@/api/mallProduct'
import { SELLER_CATEGORIES } from '@/constants/sellerData'
import {
  PRODUCT_EDIT_SURFACES,
  REJECT_REASON_TEMPLATES,
  createProductForm,
  focusTargetFromPreviewZone,
  hydrateProductForm,
  productChecklist,
  productIssues,
  productProblems,
  sectionDoneMap,
  surfaceDef,
  surfaceFromPreviewZone,
  surfaceFromSection,
  previewModeForZone,
  previewZoneVisibleOn,
  toPreviewProduct,
  toProductPayload,
  type ProductEditSurface,
  type ProductPreviewMode,
  type ProductPreviewZone,
} from '@/constants/productEditor'
import {
  loadRequestedProductQueue,
  saveRequestedProductQueue,
} from '@/utils/adminProductQueue'
import { useSameDayCutoffText } from '@/composables/useContentSnippets'
import { formatShippingChargeLabel } from '@/utils/mallSell'
import { scrollWhenReady } from '@/utils/scrollIntoContainer'

const LOG_LABEL: Record<string, string> = {
  REQUEST: '검수 요청',
  APPROVE: '승인',
  REJECT: '반려',
  EDIT: '수정',
  HIDE: '숨김',
}

const REVIEW_ACTIONS = new Set(['REQUEST', 'APPROVE', 'REJECT'])

const STATUS_TONE: Record<MallProductStatus, string> = {
  DRAFT: 'sui-badge--gray',
  REQUESTED: 'sui-badge--violet',
  SELLING: 'sui-badge--green',
  SOLDOUT: 'sui-badge--amber',
  HIDDEN: 'sui-badge--gray',
  REJECTED: 'sui-badge--red',
}

const route = useRoute()
const router = useRouter()
const { notify, load } = useAdminProducts()

const code = computed(() => String(route.params.code ?? ''))
const titleId = `aedit-title-${Math.random().toString(36).slice(2, 7)}`
const historyTitleId = `aedit-history-${Math.random().toString(36).slice(2, 7)}`
const dialogEl = ref<HTMLElement | null>(null)
const historyEl = ref<HTMLElement | null>(null)
const historyOpen = ref(false)
const screenLock = ref(false)
const screenLockText = ref('')

const product = ref<ServerMallProduct | null>(null)
const loading = ref(true)
const saving = ref(false)
const showPreview = ref(false)
const stockHistoryOpen = ref(false)
const workMode = ref<'wizard' | 'editor'>('wizard')
const wizardSeed = ref('')
const wizardRef = ref<{ resetFromForm: (asReview?: boolean) => void } | null>(null)
const editSurface = ref<ProductEditSurface>('product')
const previewMode = ref<ProductPreviewMode>('card')
const previewZone = ref<ProductPreviewZone | ''>('')
const previewTabPulse = ref<ProductPreviewMode | ''>('')
let previewTabPulseTimer: ReturnType<typeof setTimeout> | undefined
const rejectReason = ref('')
const rejectTemplates = REJECT_REASON_TEMPLATES
const queueCodes = ref<string[]>([])
const editorRef = ref<{
  focusFirstIncomplete: () => void
  focusSection: (section: string, fieldSuffix?: string) => void
  focusByZone: (zone: ProductPreviewZone | '') => void
} | null>(null)
const adminMemo = ref('')
const relatedCodes = ref<string[]>([])
const substituteCodes = ref<string[]>([])
const relatedCandidates = ref<Array<{ code: string; name: string }>>([])
const initialFormSnapshot = ref('')

const form = reactive(
  createProductForm({ category: SELLER_CATEGORIES[0], shippingFee: 3000, freeShipOver: 0 }),
)

const storeShipping = computed(() => ({
  returnFee: Math.max(0, Number(product.value?.storeReturnFee) || 0),
  cutoff: product.value?.storeCutoff || '14:00',
  returnPolicy:
    product.value?.storeReturnPolicy ||
    '당일 출고 마감 · 교환 · 반품 기준은 판매업체가 안내한 정책을 따릅니다.\n상품 하자 · 오배송 시 수령 후 판매업체 안내에 따라 교환 · 반품이 가능합니다.\n단순 변심 반품은 판매업체가 안내한 반품 배송비가 부과될 수 있습니다.',
}))

const canToggleSaleStatus = computed(() => {
  const status = product.value?.status
  return status === 'SELLING' || status === 'SOLDOUT' || status === 'HIDDEN'
})

const stockLocked = computed(() => canToggleSaleStatus.value)

function onStockLedgerUpdated(payload: { code: string; stock: number; status?: string }) {
  form.stock = payload.stock
  if (product.value) {
    product.value.stock = payload.stock
    if (payload.status) product.value.status = payload.status as MallProductStatus
  }
  notify(`재고 ${payload.stock.toLocaleString('ko-KR')}개로 반영했습니다.`)
}

function formSnapshot(): string {
  return JSON.stringify({
    form,
    adminMemo: adminMemo.value,
    relatedCodes: relatedCodes.value,
    substituteCodes: substituteCodes.value,
  })
}

function markFormClean() {
  initialFormSnapshot.value = formSnapshot()
}

const dirty = computed(() => initialFormSnapshot.value !== formSnapshot())

function labelOf(rowCode: string): string {
  const found = relatedCandidates.value.find((row) => row.code === rowCode)
  return found?.name || rowCode
}

const relatedRows = computed(() =>
  relatedCodes.value.map((rowCode) => ({ code: rowCode, name: labelOf(rowCode) })),
)
const substituteRows = computed(() =>
  substituteCodes.value.map((rowCode) => ({ code: rowCode, name: labelOf(rowCode) })),
)

function addRelated(event: Event) {
  const next = (event.target as HTMLSelectElement).value
  ;(event.target as HTMLSelectElement).value = ''
  if (!next || relatedCodes.value.includes(next) || next === code.value) return
  if (substituteCodes.value.includes(next)) {
    notify('대체상품으로 이미 지정된 코드입니다.')
    return
  }
  if (relatedCodes.value.length >= 12) {
    notify('관련상품은 최대 12개까지입니다.')
    return
  }
  relatedCodes.value = [...relatedCodes.value, next]
}

function removeRelated(target: string) {
  relatedCodes.value = relatedCodes.value.filter((rowCode) => rowCode !== target)
}

function addSubstitute(event: Event) {
  const next = (event.target as HTMLSelectElement).value
  ;(event.target as HTMLSelectElement).value = ''
  if (!next || substituteCodes.value.includes(next)) return
  if (relatedCodes.value.includes(next)) {
    notify('관련상품으로 이미 지정된 코드입니다.')
    return
  }
  if (next === code.value) return
  if (substituteCodes.value.length >= 8) {
    notify('대체상품은 최대 8개까지입니다.')
    return
  }
  substituteCodes.value = [...substituteCodes.value, next]
}

function removeSubstitute(target: string) {
  substituteCodes.value = substituteCodes.value.filter((item) => item !== target)
}

async function loadRelatedCandidates() {
  try {
    const result = await fetchAdminProducts({ status: 'SELLING' })
    relatedCandidates.value = result.products
      .filter((row) => row.code !== code.value)
      .slice(0, 200)
      .map((row) => ({ code: row.code, name: row.name }))
  } catch {
    relatedCandidates.value = []
  }
}

function onSheetStatus(status: 'SELLING' | 'SOLDOUT' | 'HIDDEN') {
  const target = { value: status }
  void onSaleStatusChange({ target } as unknown as Event)
}

async function onSaleStatusChange(event: Event) {
  const next = (event.target as HTMLSelectElement).value as MallProductStatus
  const current = product.value?.status
  if (!current || next === current || saving.value) return
  const ok = await confirmDialog({
    title: '판매 상태 변경',
    message: `판매 상태를 '${MALL_PRODUCT_STATUS_LABEL[next]}'으로 바꿀까요?`,
    confirmLabel: '변경',
    tone: 'primary',
  })
  if (!ok) {
    ;(event.target as HTMLSelectElement).value = current
    return
  }
  saving.value = true
  try {
    const { product: updated, message } = await setAdminProductStatus(code.value, next)
    product.value = updated
    notify(message || '판매 상태를 바꿨습니다.')
    await load()
  } catch (error) {
    ;(event.target as HTMLSelectElement).value = current
    notify(error instanceof Error ? error.message : '판매 상태 변경에 실패했습니다.')
  } finally {
    saving.value = false
  }
}

function applyRejectTemplate(tpl: string) {
  const next = rejectReason.value.trim() ? `${rejectReason.value.trim()}\n${tpl}` : tpl
  rejectReason.value = next.slice(0, 255)
}

function pulsePreviewTab(mode: ProductPreviewMode) {
  previewTabPulse.value = mode
  window.clearTimeout(previewTabPulseTimer)
  previewTabPulseTimer = window.setTimeout(() => {
    previewTabPulse.value = ''
  }, 1200)
}

const currentSurfaceDef = computed(() => surfaceDef(editSurface.value))
const surfaceDoneMap = computed(() => {
  const done = sectionDoneMap(form)
  const map = {} as Record<ProductEditSurface, boolean>
  for (const item of PRODUCT_EDIT_SURFACES) {
    map[item.id] = item.sections.every((id) => done[id])
  }
  return map
})

function setWorkMode(mode: 'wizard' | 'editor') {
  workMode.value = mode
  if (mode === 'editor') {
    editSurface.value = 'product'
    return
  }
  wizardRef.value?.resetFromForm(true)
}

function goSurface(surface: ProductEditSurface) {
  workMode.value = 'editor'
  const changed = editSurface.value !== surface
  editSurface.value = surface
  previewZone.value = ''
  if (surface === 'product' && previewMode.value !== 'card' && previewMode.value !== 'detail') {
    previewMode.value = 'card'
  }
  const main = dialogEl.value?.querySelector('.spform__main') as HTMLElement | null
  const side = dialogEl.value?.querySelector('.spform__side') as HTMLElement | null
  main?.scrollTo({ top: 0, behavior: 'auto' })
  side?.scrollTo({ top: 0, behavior: 'auto' })
  if (!changed) return
  const first = surfaceDef(surface).sections[0]
  if (!first) return
  void scrollWhenReady(
    () => document.getElementById(first),
    { behavior: 'auto', block: 'start', offsetTop: 16, container: main },
  )
}

function onSurfaceChange(surface: ProductEditSurface) {
  editSurface.value = surface
}

function onPreviewModeChange(mode: ProductPreviewMode) {
  if (editSurface.value === 'product') setPreviewMode(mode)
}

function setPreviewMode(mode: ProductPreviewMode) {
  previewMode.value = mode
  previewZone.value = previewZoneVisibleOn(previewZone.value, editSurface.value, mode)
}

function applyPreviewHighlight(zone: ProductPreviewZone | '') {
  const modeHint = previewModeForZone(zone)
  if (modeHint && editSurface.value === 'product' && previewMode.value !== modeHint) {
    previewMode.value = modeHint
    pulsePreviewTab(modeHint)
  } else if (modeHint && editSurface.value === 'product') {
    previewMode.value = modeHint
  }
  const visible = previewZoneVisibleOn(zone, editSurface.value, previewMode.value)
  previewZone.value = visible
  if (!visible) return

  const side = dialogEl.value?.querySelector('.spform__side') as HTMLElement | null
  if (!side) return

  if (editSurface.value === 'shipping') {
    void scrollWhenReady(
      () => side.querySelector('.spform__ship-preview') as HTMLElement | null,
      { behavior: 'auto', block: 'center', offsetTop: 120, offsetBottom: 24, container: side },
    )
    return
  }

  void scrollWhenReady(
    () => {
      const zoned = side.querySelector(`[data-zone="${visible}"]`) as HTMLElement | null
      if (zoned) return zoned
      return side.querySelector(
        previewMode.value === 'detail' ? '.spform__detail-embed' : '.spform__mall-card',
      ) as HTMLElement | null
    },
    {
      behavior: 'auto',
      block: 'center',
      offsetTop: 130,
      offsetBottom: 24,
      container: side,
    },
    480,
  )
}

function onPreviewZone(zone: ProductPreviewZone | '') {
  applyPreviewHighlight(zone)
}

function onCardEditZone(zone: ProductPreviewZone, fieldSuffix?: string) {
  const next = surfaceFromPreviewZone(zone)
  if (next) editSurface.value = next
  applyPreviewHighlight(zone)
  if (fieldSuffix) {
    const target = focusTargetFromPreviewZone(zone)
    if (target) editorRef.value?.focusSection(target.section, fieldSuffix)
    else editorRef.value?.focusByZone(zone)
  } else {
    editorRef.value?.focusByZone(zone)
  }
}

function onHotkey(event: KeyboardEvent) {
  if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 's') return
  event.preventDefault()
  void onSave({ skipConfirm: true })
}

function onWindowHotkey(event: KeyboardEvent) {
  onHotkey(event)
}

async function ensureRequestedQueue() {
  let codes = loadRequestedProductQueue()
  if (!codes.length || !codes.includes(code.value)) {
    try {
      const result = await fetchAdminProducts({ status: 'REQUESTED' })
      codes = result.products.map((row) => row.code)
      if (codes.length) saveRequestedProductQueue(codes)
    } catch {
      codes = codes.length ? codes : []
    }
  }
  queueCodes.value = codes
}

function onEsc() {
  if (saving.value || screenLock.value) return
  if (historyOpen.value) {
    historyOpen.value = false
    return
  }
  void onClose()
}

watch(historyOpen, (open) => {
  if (!open) return
  void nextTick(() => historyEl.value?.focus())
})

async function loadProduct() {
  historyOpen.value = false
  loading.value = true
  editSurface.value = 'product'
  previewMode.value = 'card'
  previewZone.value = ''
  try {
    const detail = await fetchAdminProduct(code.value)
    product.value = detail
    hydrateProductForm(form, detail)
    adminMemo.value = detail.adminMemo || ''
    relatedCodes.value = [...(detail.relatedCodes || [])]
    substituteCodes.value = [...(detail.substituteCodes || [])]
    rejectReason.value = detail.status === 'REJECTED' ? detail.rejectReason : ''
    markFormClean()
    workMode.value = 'wizard'
    wizardSeed.value = `${code.value}-${Date.now()}`
    void nextTick(() => wizardRef.value?.resetFromForm(true))
    void loadRelatedCandidates()
    if (detail.status === 'REQUESTED') {
      await ensureRequestedQueue()
    } else {
      queueCodes.value = []
    }
  } catch (error) {
    notify(error instanceof Error ? error.message : '상품을 불러오지 못했습니다.')
    router.replace('/admin/products')
  } finally {
    loading.value = false
    requestAnimationFrame(() => dialogEl.value?.focus())
  }
}

watch(code, () => {
  void loadProduct()
}, { immediate: true })

const showQueueNav = computed(
  () => product.value?.status === 'REQUESTED' && queueCodes.value.length > 1,
)
const queueIndex = computed(() => queueCodes.value.indexOf(code.value))
const prevCode = computed(() =>
  queueIndex.value > 0 ? queueCodes.value[queueIndex.value - 1] : '',
)
const nextCode = computed(() =>
  queueIndex.value >= 0 && queueIndex.value < queueCodes.value.length - 1
    ? queueCodes.value[queueIndex.value + 1]
    : '',
)

function goQueue(target: string) {
  if (!target || target === code.value || saving.value) return
  router.push(`/admin/products/${target}`)
}

function onProblemJump() {
  workMode.value = 'editor'
  void nextTick(() => editorRef.value?.focusFirstIncomplete())
}

function scrollToCheck(section: string) {
  const next = surfaceFromSection(section)
  if (next) editSurface.value = next
  editorRef.value?.focusSection(section)
}

const logs = computed(() => product.value?.logs ?? [])
const reviewLogs = computed(() => logs.value.filter((log) => REVIEW_ACTIONS.has(log.action)))
const otherLogs = computed(() => logs.value.filter((log) => !REVIEW_ACTIONS.has(log.action)))
const requestCount = computed(() => {
  const fromLogs = logs.value.filter((log) => log.action === 'REQUEST').length
  return Math.max(fromLogs, product.value?.requestCount ?? 0)
})

function logLabel(log: { action: string }, index: number): string {
  if (log.action !== 'REQUEST') return LOG_LABEL[log.action] || log.action
  const total = reviewLogs.value.filter((item) => item.action === 'REQUEST').length
  if (total <= 1) return '검수 요청'
  const requestOrder = reviewLogs.value
    .slice(0, index + 1)
    .filter((item) => item.action === 'REQUEST').length
  return `${total - requestOrder + 1}차 검수 요청`
}

const checklist = computed(() => productChecklist(form))
const checklistOk = computed(() => checklist.value.filter((rule) => rule.ok).length)
const checklistPct = computed(() =>
  checklist.value.length ? Math.round((checklistOk.value / checklist.value.length) * 100) : 0,
)
const hardIssues = computed(() => productIssues(form))
const firstFailedCheck = computed(() => checklist.value.find((rule) => !rule.ok) || null)

const shippingFeeLabel = computed(() => formatShippingChargeLabel(form))
const returnFeeLabel = computed(() => {
  const fee = Math.max(0, Number(storeShipping.value.returnFee) || 0)
  return fee > 0 ? `${fee.toLocaleString('ko-KR')}원` : '무료'
})
const { sameDayCutoffText, loadSameDayCutoffSnippet } = useSameDayCutoffText(
  () => storeShipping.value.cutoff,
)
const policyLines = computed(() =>
  String(storeShipping.value.returnPolicy || '')
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean),
)

const problems = computed(() => productProblems(form))
const firstProblem = computed(() => problems.value[0] ?? '')
const canReview = computed(() => product.value?.status === 'REQUESTED')
const canApprove = computed(() => canReview.value && hardIssues.value.length === 0)

const statusTone = computed(() =>
  product.value ? STATUS_TONE[product.value.status] ?? 'sui-badge--gray' : 'sui-badge--gray',
)

const previewProduct = computed(() =>
  toPreviewProduct(form, {
    code: code.value,
    storeName: product.value?.storeName ?? '',
    storeCutoff: storeShipping.value.cutoff,
    storeReturnFee: storeShipping.value.returnFee,
    storeReturnPolicy: storeShipping.value.returnPolicy,
  }),
)

async function onClose() {
  if (saving.value || screenLock.value) return
  if (dirty.value) {
    const ok = await confirmDialog({
      title: '작성 내용 확인',
      message: '저장하지 않은 상품 변경 내용이 있습니다. 닫을까요?',
      confirmLabel: '닫기',
      tone: 'danger',
    })
    if (!ok) return
  }
  router.push('/admin/products')
}

/** 저장은 상태를 건드리지 않는다. 판매로 올리는 건 승인 버튼이 한다. */
async function persist(): Promise<ServerMallProduct | null> {
  const status = product.value?.status ?? 'REQUESTED'
  const payload = {
    ...toProductPayload(form, status),
    adminMemo: adminMemo.value.trim(),
  }
  await saveAdminProduct(code.value, payload)
  const withRelated = await saveAdminProductRelated(code.value, {
    relatedCodes: relatedCodes.value,
    substituteCodes: substituteCodes.value,
  })
  product.value = withRelated
  adminMemo.value = withRelated.adminMemo || ''
  relatedCodes.value = [...(withRelated.relatedCodes || [])]
  substituteCodes.value = [...(withRelated.substituteCodes || [])]
  markFormClean()
  return withRelated
}

async function onSave(options: { skipConfirm?: boolean } = {}) {
  if (saving.value) return
  if (!options.skipConfirm) {
    const ok = await confirmDialog({
      title: '상품 수정 저장',
      message: '수정 내용을 저장할까요?',
      confirmLabel: '저장',
      tone: 'primary',
    })
    if (!ok) return
  }
  saving.value = true

  try {
    await persist()
    notify('수정 내용을 저장했습니다.')
  } catch (error) {
    notify(error instanceof Error ? error.message : '저장에 실패했습니다.')
  } finally {
    saving.value = false
  }
}

async function onApprove() {
  if (saving.value) return

  if (!canApprove.value) {
    notify(firstProblem.value)
    editorRef.value?.focusFirstIncomplete()
    return
  }
  const ok = await confirmDialog({
    title: '상품 판매 승인',
    message: '이 상품을 판매 승인할까요? 함우리몰에 바로 노출됩니다.',
    confirmLabel: '승인',
    tone: 'primary',
  })
  if (!ok) return

  saving.value = true
  screenLock.value = true
  screenLockText.value = '저장하고 올리는 중입니다'
  historyOpen.value = false

  try {
    await persist()
    const { product: approved, message } = await approveAdminProduct(code.value)
    product.value = approved
    notify(message || '판매 승인했습니다.')
    await load()
    router.push('/admin/products')
  } catch (error) {
    notify(error instanceof Error ? error.message : '승인에 실패했습니다.')
    saving.value = false
    screenLock.value = false
  }
}

async function onReject() {
  if (saving.value) return

  const reason = rejectReason.value.trim()

  if (!reason) {
    notify('반려 사유를 적어 주세요.')
    return
  }
  const ok = await confirmDialog({
    title: '상품 반려',
    message: '이 상품을 반려할까요? 사유가 판매업체에 전달됩니다.',
    confirmLabel: '반려',
    tone: 'danger',
  })
  if (!ok) return

  saving.value = true

  try {
    product.value = await rejectAdminProduct(code.value, reason)
    notify('반려했습니다. 사유가 판매업체에 전달됩니다.')
    await load()
    router.push('/admin/products')
  } catch (error) {
    notify(error instanceof Error ? error.message : '반려에 실패했습니다.')
  } finally {
    saving.value = false
  }
}

function formatDateTime(value?: string | null): string {
  if (!value) return '-'
  const date = new Date(value)
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(
    date.getDate(),
  ).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

onMounted(() => {
  document.body.style.overflow = 'hidden'
  window.addEventListener('keydown', onWindowHotkey)
  void loadSameDayCutoffSnippet()
})

onUnmounted(() => {
  document.body.style.overflow = ''
  window.removeEventListener('keydown', onWindowHotkey)
  window.clearTimeout(previewTabPulseTimer)
})
</script>

<style lang="scss" src="./AdminProductEditPage.scss"></style>
